import * as React from 'react';
import { useWs, type WsInvalidationMap } from './hook';
import type { WsStatus } from './socket';
import type { WsEvent, WsEventType } from './events';

/**
 * Provider-shaped API over the `useWs` hook.
 *
 * The admin SPA composes a <WsProvider> at its root and subscribes deeper in the tree with
 * useWsEvent / useWsStatus. The buyer SPA uses `useWs` directly. Both shapes are legitimate;
 * what is NOT legitimate is each app shipping its own socket client, because then there are two
 * reconnect policies, two liveness indicators, and two places where the rule below can be
 * forgotten by whoever touches it next.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE ANONYMITY RULE FOR WEBSOCKETS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A websocket frame is an OUTBOUND SURFACE, exactly like an API response, and it is built by
 * the SAME audience-specific DTO mapper on the server.
 *
 * `order.created` for the admin and `order.created` for a producer are TWO DIFFERENT MESSAGES,
 * from two different mappers, published to two different Redis channels. There is never one
 * fat payload that the client filters down — a client cannot hide what it has already
 * received, and "the UI doesn't render it" is not a security boundary, it is a rendering
 * decision.
 *
 * Which is why the frames that arrive here are deliberately THIN: an event type and an id.
 * They are a nudge to refetch, not a data channel. The refetch then goes through the normal
 * authenticated endpoint, which applies the normal guards.
 */

type WsContextValue = {
  status: WsStatus;
  subscribe: (type: WsEventType, handler: (event: WsEvent) => void) => () => void;
};

const WsContext = React.createContext<WsContextValue | null>(null);

export type WsProviderProps = {
  /** Query keys to invalidate per event type — the nudge-to-refetch map. */
  invalidate?: WsInvalidationMap;
  enabled?: boolean;
  children: React.ReactNode;
};

export function WsProvider({ invalidate = {}, enabled = true, children }: WsProviderProps) {
  // A Set of handlers per event type. A ref, not state: adding a subscriber must not re-render
  // the whole tree under the provider.
  const handlers = React.useRef(new Map<WsEventType, Set<(e: WsEvent) => void>>());

  const { status } = useWs({
    enabled,
    invalidate,
    onEvent: (event: WsEvent) => {
      const set = handlers.current.get(event.type as WsEventType);
      if (!set) return;
      for (const h of set) h(event);
    },
  });

  const subscribe = React.useCallback(
    (type: WsEventType, handler: (event: WsEvent) => void) => {
      let set = handlers.current.get(type);
      if (!set) {
        set = new Set();
        handlers.current.set(type, set);
      }
      set.add(handler);

      return () => {
        set?.delete(handler);
      };
    },
    [],
  );

  const value = React.useMemo(() => ({ status, subscribe }), [status, subscribe]);

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

function useWsContext(hook: string): WsContextValue {
  const ctx = React.useContext(WsContext);
  if (!ctx) {
    throw new Error(`${hook}() must be used inside <WsProvider>`);
  }
  return ctx;
}

/** Subscribe to one event type for the lifetime of the component. */
export function useWsEvent(type: WsEventType, handler: (event: WsEvent) => void): void {
  const { subscribe } = useWsContext('useWsEvent');

  // Keep the latest handler in a ref so that an inline closure does not tear down and
  // re-establish the subscription on every render.
  const ref = React.useRef(handler);
  React.useEffect(() => {
    ref.current = handler;
  }, [handler]);

  React.useEffect(() => subscribe(type, (e) => ref.current(e)), [subscribe, type]);
}

/**
 * Connection liveness, for the "live" indicator in the admin top bar.
 *
 * Worth surfacing rather than hiding: when the socket is down, Merdan's queue counters have
 * silently stopped updating, and a stale "0 new orders" is far more dangerous than a visible
 * "reconnecting…". The dashboard's whole job is to tell him an order arrived.
 *
 * Returns the status STRING, not a `{ connected }` object. The two are not interchangeable and
 * conflating them is what pinned the top bar to a permanent "нет связи": the indicator needs to
 * distinguish "reconnecting, hold on" from "down, the numbers are stale", and a boolean cannot.
 */
export function useWsStatus(): WsStatus {
  return useWsContext('useWsStatus').status;
}
